import { cs } from '../../lib/currency';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import api from '../../services/api';
import { toast } from 'sonner';
import { 
  Calendar, 
  DollarSign,
  Eye,
  Users,
  Receipt
} from 'lucide-react';

const statusColors = {
  future: 'bg-blue-100 text-blue-700',
  active: 'bg-green-100 text-green-700',
  closed: 'bg-yellow-100 text-yellow-700',
  past: 'bg-gray-100 text-gray-700',
};

const registrationStatusColors = {
  pending: 'bg-yellow-100 text-yellow-700',
  selected: 'bg-blue-100 text-blue-700',
  payment_pending: 'bg-orange-100 text-orange-700',
  confirmed: 'bg-green-100 text-green-700',
  not_selected: 'bg-gray-100 text-gray-700',
  cancelled: 'bg-red-100 text-red-700',
};

const StaffTournaments = () => {
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [expenseSummary, setExpenseSummary] = useState(null);
  const [activeTab, setActiveTab] = useState('active');

  useEffect(() => {
    fetchTournaments();
  }, []);

  const fetchTournaments = async () => {
    try {
      const res = await api.get('/tournaments');
      setTournaments(res.data);
    } catch (error) {
      toast.error('Failed to load tournaments');
    } finally {
      setLoading(false);
    }
  };

  const _fetchTournamentDetails = async (tournament) => {
    setSelectedTournament(tournament);
    try {
      const [registrationsRes, expensesRes, summaryRes] = await Promise.all([
        api.get(`/tournaments/${tournament.id}/registrations`),
        api.get(`/tournaments/${tournament.id}/expenses`),
        api.get(`/tournaments/${tournament.id}/expenses/summary`),
      ]);
      setRegistrations(registrationsRes.data);
      setExpenses(expensesRes.data);
      setExpenseSummary(summaryRes.data);
      setShowDetailsDialog(true);
    } catch (error) {
      toast.error('Failed to load tournament details');
    }
  };

  const filteredTournaments = tournaments.filter(t => {
    if (activeTab === 'all') return true;
    return t.status === activeTab;
  });

  if (loading) {
    return (
      <DashboardLayout title="Tournaments">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Tournaments">
      <div className="space-y-6" data-testid="staff-tournaments">
        <p className="text-slate-600">View tournament registrations and details</p>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="all">All ({tournaments.length})</TabsTrigger>
            <TabsTrigger value="active">Active ({tournaments.filter(t => t.status === 'active').length})</TabsTrigger>
            <TabsTrigger value="future">Future ({tournaments.filter(t => t.status === 'future').length})</TabsTrigger>
            <TabsTrigger value="past">Past ({tournaments.filter(t => t.status === 'past').length})</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-6">
            {filteredTournaments.length === 0 ? (
              <Card>
                <CardContent className="p-6 lg:p-8 text-center text-slate-500">
                  No tournaments found
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {filteredTournaments.map((tournament) => (
                  <Card key={tournament.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4 lg:p-6">
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <button
                              type="button"
                              onClick={() => navigate(`/admin/tournaments/${tournament.id}`)}
                              className="text-sm lg:text-lg font-semibold text-left hover:underline focus:outline-none focus:ring-2 focus:ring-blue-300 rounded"
                              data-testid={`tournament-name-${tournament.id}`}
                            >
                              {tournament.name}
                            </button>
                            <Badge className={`text-xs ${statusColors[tournament.status]}`}>
                              {tournament.status}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {tournament.location_type === 'out_of_state' ? 'Out of State' : 'Local'}
                            </Badge>
                          </div>
                          
                          <div className="flex flex-wrap gap-3 lg:gap-4 text-xs lg:text-sm text-slate-600">
                            <span className="flex items-center gap-1 whitespace-nowrap">
                              <Calendar className="h-3.5 w-3.5 lg:h-4 lg:w-4 shrink-0" />
                              {tournament.start_date} — {tournament.end_date}
                            </span>
                            <span className="flex items-center gap-1 whitespace-nowrap">
                              <DollarSign className="h-3.5 w-3.5 lg:h-4 lg:w-4 shrink-0" />
                              {cs()}{tournament.initial_payment_amount} initial
                            </span>
                          </div>

                          <div className="flex flex-wrap gap-1">
                            {tournament.age_categories.map(cat => (
                              <Badge key={cat} variant="secondary" className="text-xs">
                                {cat.toUpperCase()}
                              </Badge>
                            ))}
                          </div>
                        </div>

                        <Button
                          variant="outline"
                          onClick={() => navigate(`/admin/tournaments/${tournament.id}`)}
                          data-testid={`view-tournament-${tournament.id}`}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          View Details
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Tournament Details Dialog */}
        <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedTournament?.name}</DialogTitle>
              <DialogDescription>
                {selectedTournament?.start_date} to {selectedTournament?.end_date}
              </DialogDescription>
            </DialogHeader>

            <Tabs defaultValue="registrations" className="mt-4">
              <TabsList>
                <TabsTrigger value="registrations">
                  <Users className="h-4 w-4 mr-1" />
                  Registrations ({registrations.length})
                </TabsTrigger>
                <TabsTrigger value="expenses">
                  <Receipt className="h-4 w-4 mr-1" />
                  Expenses ({expenses.length})
                </TabsTrigger>
                <TabsTrigger value="summary">
                  <DollarSign className="h-4 w-4 mr-1" />
                  Summary
                </TabsTrigger>
              </TabsList>

              <TabsContent value="registrations" className="mt-4">
                <div className="space-y-4">
                  {registrations.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">No registrations yet</p>
                  ) : (
                    <div className="divide-y">
                      {registrations.map(reg => (
                        <div key={reg.id} className="py-4 flex items-center justify-between">
                          <div>
                            <p className="font-medium">{reg.player_name}</p>
                            <p className="text-sm text-slate-500">
                              Parent: {reg.parent_name} • Category: {reg.age_category.toUpperCase()}
                            </p>
                            <div className="flex gap-2 mt-1">
                              <Badge variant="outline">
                                {reg.can_travel ? 'Can Travel' : 'Cannot Travel'}
                              </Badge>
                              <Badge variant="outline">
                                Availability: {reg.availability_status}
                              </Badge>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge className={registrationStatusColors[reg.status]}>
                              {reg.status.replace('_', ' ')}
                            </Badge>
                            {reg.payment_status === 'paid' && (
                              <Badge className="bg-green-100 text-green-700">
                                Paid {cs()}{reg.payment_amount}
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="expenses" className="mt-4">
                <div className="space-y-4">
                  {expenses.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">No expenses recorded yet</p>
                  ) : (
                    <div className="divide-y">
                      {expenses.map(expense => (
                        <div key={expense.id} className="py-3 flex items-center justify-between">
                          <div>
                            <p className="font-medium capitalize">
                              {expense.category.replace('_', ' ')}
                            </p>
                            {expense.description && (
                              <p className="text-sm text-slate-500">{expense.description}</p>
                            )}
                          </div>
                          <span className="font-semibold">{cs()}{expense.amount.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="summary" className="mt-4">
                {expenseSummary && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <Card>
                        <CardContent className="p-4 text-center">
                          <p className="text-sm text-slate-500">Total Expenses</p>
                          <p className="text-2xl font-bold text-red-600">
                            {cs()}{expenseSummary.total_expenses.toFixed(2)}
                          </p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="p-4 text-center">
                          <p className="text-sm text-slate-500">Total Collected</p>
                          <p className="text-2xl font-bold text-green-600">
                            {cs()}{expenseSummary.total_collected.toFixed(2)}
                          </p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="p-4 text-center">
                          <p className="text-sm text-slate-500">Confirmed Players</p>
                          <p className="text-2xl font-bold">{expenseSummary.confirmed_players}</p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="p-4 text-center">
                          <p className="text-sm text-slate-500">Balance</p>
                          <p className={`text-2xl font-bold ${expenseSummary.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {cs()}{expenseSummary.balance.toFixed(2)}
                          </p>
                        </CardContent>
                      </Card>
                    </div>

                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Expenses by Category</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {Object.entries(expenseSummary.by_category).map(([cat, amount]) => (
                            <div key={cat} className="flex justify-between">
                              <span className="capitalize">{cat.replace('_', ' ')}</span>
                              <span className="font-medium">{cs()}{amount.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default StaffTournaments;
